import React, { useContext, useState, useEffect } from "react"

import { Formik, Form, Field } from "formik"
import * as Yup from "yup"

import { Link } from "gatsby"

import TextArea from "./Form/TextArea"
import Input from "./Form/Input"
import Checkbox from "./Form/Checkbox"
import Select from "./Form/Select"
import ErrorMessage from "./Form/ErrorMessage"

import { FormButton } from "./Button"
import Grid from "./Grid"

import { GlobalStateContext, GlobalDispatchContext } from "../context/GlobalStateProvider"

import FreeEmailDomains from "./freeEmailDomains.json"

const fleetSizeOptions = [
	{ value: "none", text: "Fleet size", options: { disabled: true, hidden: true } },
	{ value: "small", text: "1 - 9 vessels" },
	{ value: "medium", text: "10 - 49 vessels" },
	{ value: "large", text: "50+ vessels" },
]

const titleOptions = [
	{ value: "none", text: "Job title", options: { disabled: true, hidden: true } },
	{ value: "captain_master", text: "Captain/Master" },
	{ value: "ceo_president", text: "CEO/President" },
	{ value: "crew_manager", text: "Crew Manager" },
	{ value: "dpa", text: "Designated Person Ashore (DPA)" },
	{ value: "fleet_manager", text: "Fleet Manager" },
	{ value: "it_staff", text: "IT Staff" },
	{ value: "officer", text: "Officer" },
	{ value: "operations_manager", text: "Operations Manager" },
	{ value: "partner_reseller", text: "Partner/Reseller" },
	{ value: "qhse_manager", text: "QHSE Manager" },
	{ value: "student", text: "Student" },
	{ value: "superintendent", text: "Superintendent" },
	{ value: "technical_manager", text: "Technical Manager" },
	{ value: "vessel_staff", text: "Vessel Staff" },
	{ value: "other", text: "Other" },
]

const ScheduleDemoFormValidationSchema = Yup.object({
	company: Yup.string().required("Sorry, your company name is required"),
	firstName: Yup.string().required("Sorry, your first name is required"),
	lastName: Yup.string().required("Sorry, your last name is required"),
	title: Yup.string()
		.oneOf(
			titleOptions.slice(1).map(o => o.value),
			"Please select your job title",
		)
		.required(),
	fleetSize: Yup.string()
		.oneOf(
			fleetSizeOptions.slice(1).map(o => o.value),
			"Please select your approximate fleet size",
		)
		.required(),
	email: Yup.string()
		.required("Sorry, your work email address is required")
		.email("Invalid email format")
		.test(
			"email-non-free-domain",
			"Please use your work e-mail address",
			email => email && !FreeEmailDomains.includes(email.substring(email.indexOf("@") + 1)),
		),

	// subscribeNewsletter: Yup.boolean().oneOf([true, false]),
	actOnBehalf: Yup.boolean()
		.oneOf(
			[true],
			"You need confirm that you act on behalf of a shipping company or a ship managing company",
		)
		.required(),
	privacy: Yup.boolean().oneOf([true], "The privacy statement must be accepted").required(),
})

const ScheduleDemoFormInitialValues = {
	company: "",
	firstName: "",
	lastName: "",
	title: titleOptions[0].value,
	email: "",
	message: "",
	fleetSize: fleetSizeOptions[0].value,
	actOnBehalf: false,
	privacy: false,
	// subscribeNewsletter: false,
}

const ScheduleDemoForm = ({
	cta = "Schedule a demo",
	children,
	className = "",
	onSubmitHandler,
	skipValidation = false,
	initialValues = {},
	pretext = "",
}) => {
	const state = useContext(GlobalStateContext)
	const { setState } = useContext(GlobalDispatchContext)

	// State to manage Formik initial values
	const [formInitialValues, setFormInitialValues] = useState({
		...ScheduleDemoFormInitialValues,
		...initialValues,
	})

	useEffect(() => {
		if (state && state.utmParams) {
			setFormInitialValues(prevValues => ({
				...prevValues,
				...state.utmParams,
			}))
		}
	}, [state, state?.utmParams])

	useEffect(() => {
		if (initialValues.campaign) {
			// Store campaign value in context state for later use
			setState(prevState => ({
				...prevState,
				campaign: initialValues.campaign,
			}))
		}
	}, [initialValues.campaign, setState])

	const handleOnSubmit = async (values, { setSubmitting, resetForm }) => {
		const utmParams = Object.entries(state.utmParams || {}).map(([key, value]) => ({
			key: value,
		}))
		const campaign = state.campaign ? state.campaign : null

		const payload = {
			...values,
			...utmParams,
			...(campaign != null && { campaign }),
		}

		await window
			.fetch(`/api/demo`, {
				method: `POST`,
				headers: {
					"content-type": "application/json",
				},
				body: JSON.stringify(payload),
			})
			.then(response => response.json())
			.then(res => {
				setSubmitting(false)
				if (res.result === "OK") {
					resetForm()
					onSubmitHandler()
				} else {
					// TODO Handle errors
				}
			})
	}

	return (
		<div className={className}>
			<h3 className="display3 mb-20">Schedule a demo</h3>
			{pretext && <p className="mb-20">{pretext}</p>}
			<Formik
				validateOnBlur={true}
				validateOnChange={true}
				enableReinitialize={true}
				initialValues={formInitialValues}
				validationSchema={skipValidation ? null : ScheduleDemoFormValidationSchema}
				onSubmit={handleOnSubmit}>
				{({ dirty, isValid, isSubmitting, handleOnSubmit }) => (
					<Form>
						<Grid withContainer={false} columns={2}>
							<div className="mb-30 col-span-2">
								<Input name="company" label="Company" value="Hanseaticsoft" />
								<ErrorMessage name="company" />
							</div>
							<div className="mb-30 col-span-2">
								<Select
									name="fleetSize"
									label="Fleet size"
									options={fleetSizeOptions}
								/>
								<ErrorMessage name="fleetSize" />
							</div>
							<div className="mb-30 col-span-2 lg:col-span-1">
								<Input name="firstName" label="First name" />
								<ErrorMessage name="firstName" />
							</div>
							<div className="mb-30 col-span-2 lg:col-span-1">
								<Input name="lastName" label="Last name" />
								<ErrorMessage name="lastName" />
							</div>
							<div className="mb-30 col-span-2 lg:col-span-2">
								<Select name="title" label="Job title" options={titleOptions} />
								<ErrorMessage name="title" />
							</div>
							<div className="mb-30 col-span-2">
								<Input name="email" label="E-mail address" />
								<ErrorMessage name="email" />
							</div>
							<div className="mb-30 col-span-2">
								<TextArea name="message" label="Message" required={false} />
							</div>
							<div className="mb-30 col-span-2">
								<Checkbox
									name="actOnBehalf"
									label="I confirm that I act on behalf of a shipping company or ship managing company."
								/>
								<ErrorMessage name="actOnBehalf" />
							</div>
							<div className="mb-30 col-span-2">
								<Checkbox
									name="privacy"
									label="I confirm that I have read and agree to the privacy statement."
								/>
								<ErrorMessage name="privacy" />
							</div>
							{/*
								<div className="col-span-2">
									<Checkbox
										name="subscribeNewsletter"
										label="I want to subscribe to the Hanseaticsoft marketing newsletter"
									/>
								</div>
							*/}
						</Grid>

						{children}

						<FormButton
							type="submit"
							className="w-full lg:w-auto xl:w-full"
							primary={false}
							disabled={isSubmitting} // TODO !dirty ||
							label={isSubmitting ? "Please wait..." : cta}
						/>
					</Form>
				)}
			</Formik>
		</div>
	)
}

export const ScheduleDemoAlternativeForm = ({
	cta = "Schedule a demo",
	children,
	onSubmitHandler,
	className = "",
	initialValues = {},
}) => {
	const state = useContext(GlobalStateContext)
	const { setState } = useContext(GlobalDispatchContext)

	// State to manage Formik initial values
	const [formInitialValues, setFormInitialValues] = useState({
		...ScheduleDemoFormInitialValues,
		...initialValues,
	})

	useEffect(() => {
		if (state && state.utmParams) {
			setFormInitialValues(prevValues => ({
				...prevValues,
				...state.utmParams,
			}))
		}
	}, [state, state?.utmParams])

	useEffect(() => {
		if (initialValues.campaign) {
			// Store campaign value in context state for later use
			setState(prevState => ({
				...prevState,
				campaign: initialValues.campaign,
			}))
		}
	}, [initialValues.campaign, setState])

	const handleOnSubmit = async (values, { setSubmitting, resetForm }) => {
		const utmParams = Object.entries(state.utmParams || {}).map(([key, value]) => ({
			key: value,
		}))
		const campaign = state.campaign ? state.campaign : null

		const payload = {
			...values,
			...utmParams,
			...(campaign != null && { campaign }),
		}

		await window
			.fetch(`/api/demo`, {
				method: `POST`,
				headers: {
					"content-type": "application/json",
				},
				body: JSON.stringify(payload),
			})
			.then(response => response.json())
			.then(res => {
				setSubmitting(false)
				if (res.result === "OK") {
					resetForm()
					onSubmitHandler()
				} else {
					// TODO Handle errors
				}
			})
	}

	return (
		<div className={className}>
			<Formik
				validateOnBlur={true}
				validateOnChange={true}
				enableReinitialize={true}
				initialValues={formInitialValues}
				validationSchema={ScheduleDemoFormValidationSchema}
				onSubmit={handleOnSubmit}>
				{({ dirty, isValid, isSubmitting, handleOnSubmit }) => (
					<Form>
						<div className="flex flex-col gap-y-40">
							<Grid>
								<div className="col-span-10 col-start-2">
									<h2 className="display2">Schedule a demo</h2>
								</div>
							</Grid>

							<Grid className="gap-20">
								<div className="col-start-2 col-end-12 lg:col-end-7">
									<Input name="company" label="Company" value="Hanseaticsoft" />
									<ErrorMessage name="company" />
								</div>
								<div className="col-start-2 col-end-12 lg:col-start-7 lg:col-end-12">
									<Select
										name="fleetSize"
										label="Fleet size"
										options={fleetSizeOptions}
									/>
									<ErrorMessage name="fleetSize" />
								</div>
								<div className="col-start-2 col-end-12 lg:col-end-7">
									<Input name="firstName" label="First name" />
									<ErrorMessage name="firstName" />
								</div>
								<div className="col-start-2 col-end-12 lg:col-start-7 lg:col-end-12">
									<Input name="lastName" label="Last name" />
									<ErrorMessage name="lastName" />
								</div>

								<div className="col-start-2 col-span-10">
									<Select name="title" label="Job title" options={titleOptions} />
									<ErrorMessage name="title" />
								</div>

								<div className="col-start-2 col-span-10">
									<Input name="email" label="E-mail address" />
									<ErrorMessage name="email" />
								</div>
								<div className="col-start-2 col-span-10">
									<TextArea name="message" label="Message" required={false} />
								</div>
							</Grid>
							<Grid className="gap-20">
								<div className="col-span-10 col-start-2">
									<Checkbox name="privacy">
										I confirm that I have read and understood the{" "}
										<Link
											to="/privacy"
											className="text-tealblue underline hover:text-pictonblue">
											privacy statement
										</Link>
									</Checkbox>
									<ErrorMessage name="privacy" />
								</div>
								<div className="col-span-10 col-start-2">
									<Checkbox name="actOnBehalf">
										I confirm that I act on behalf of a shipping company or ship
										managing company
									</Checkbox>
									<ErrorMessage name="actOnBehalf" />
								</div>

								{/*
										<div className="col-span-10 col-start-2">
											<Checkbox name="subscribeNewsletter" label="I want to subscribe to the Hanseaticsoft marketing newsletter" />
										</div>
									*/}
							</Grid>
							<Grid className="">
								<div className="col-span-10 col-start-2">{children}</div>
							</Grid>

							<Grid className="">
								<div className="col-span-10 col-start-2">
									<FormButton
										type="submit"
										className="w-full md:w-auto"
										primary={true}
										disabled={isSubmitting}
										label={isSubmitting ? "Please wait..." : cta}
									/>
								</div>
							</Grid>
						</div>
					</Form>
				)}
			</Formik>
		</div>
	)
}

export default ScheduleDemoForm
